import mcpGuide from './mcp-guide.json';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  FileJson,
  Languages,
  Menu,
  Moon,
  Search,
  Sun,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  endpointFromPath,
  endpointHref,
  endpoints,
  groupsFor,
  groupsOf,
  landingEndpoint,
  orderEndpoints,
  productOf,
  products,
  sectionOf,
  sectionsFor,
  subsectionOf,
  subsectionsFor,
  type Endpoint,
  type Product,
} from './data';
import {
  canonicalUrl,
  type Locale,
  LOCALES,
  localeFromPath,
  localeInfo,
  localizeEndpoint,
  stripLocale,
  themeLabel,
  ui,
  withLocale,
} from './i18n';

const API_BASE = 'https://api.eroya.co/v1';
// The console, data store, and status board are all served by the Flutter app
// on app.eroya.co. The apex eroya.co now serves the static marketing site, so
// linking there for /console etc. 404s.
const APP_URL = 'https://app.eroya.co';

type Strings = (typeof ui)[Locale];

function currentEndpoint(): Endpoint {
  return endpointFromPath(stripLocale(window.location.pathname)) ?? endpoints[0];
}

function navigate(path: string) {
  if (window.location.pathname === path) return;
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo({ top: 0, behavior: 'instant' });
}

/** Navigate to an endpoint within the active locale. */
function goToEndpoint(endpoint: Endpoint, locale: Locale) {
  navigate(withLocale(endpointHref(endpoint), locale));
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="alternate"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let link = document.head.querySelector<HTMLLinkElement>(selector);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    if (hreflang) link.hreflang = hreflang;
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * The reference is a single-page app, so title, canonical, description, and the
 * hreflang graph are rewritten on every navigation. Without this a crawler sees
 * one set of tags for all twenty endpoints in all four languages.
 */
function syncDocumentHead(endpoint: Endpoint, locale: Locale, t: Strings, path = endpointHref(endpoint)) {
  document.title = `${endpoint.title} — ${t.titleSuffix}`;
  document.documentElement.lang = localeInfo[locale].htmlLang;

  upsertLink('canonical', canonicalUrl(path, locale));
  for (const other of LOCALES) {
    const href = canonicalUrl(path, other);
    for (const tag of [localeInfo[other].hreflang, ...localeInfo[other].hreflangAliases]) {
      upsertLink('alternate', href, tag);
    }
  }
  upsertLink('alternate', canonicalUrl(path, 'en'), 'x-default');

  const description = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (description) {
    const text = `${endpoint.summary} ${endpoint.description}`.trim();
    description.content = text.length > 300 ? `${text.slice(0, 297)}…` : text;
  }
}

function Brand({ locale, t }: { locale: Locale; t: Strings }) {
  return (
    <button className="brand" onClick={() => goToEndpoint(endpoints[0], locale)} aria-label="Eroya documentation home">
      <img src="/eroya-logo.svg" alt="" aria-hidden="true" />
      <span className="brand-section">{t.docsLabel}</span>
    </button>
  );
}

function Sidebar({
  selected,
  isMcp,
  product,
  locale,
  t,
  localized,
  open,
  close,
}: {
  selected: Endpoint;
  isMcp: boolean;
  product: Product;
  locale: Locale;
  t: Strings;
  localized: Endpoint[];
  open: boolean;
  close: () => void;
}) {
  const flatFileUtilities = localized.filter((endpoint) =>
    ['flatfiles-datasets', 'flatfiles-list', 'flatfiles-url', 'flatfiles-download'].includes(endpoint.id),
  );
  const flatFileAssets = [
    { label: t.groups['U.S. Stocks'], prefix: 'flatfiles-stocks-' },
    { label: t.groups['U.S. Options'], prefix: 'flatfiles-options-' },
    { label: t.groups['U.S. Indices'], prefix: 'flatfiles-indices-' },
  ].map((asset) => ({
    ...asset,
    endpoints: localized.filter((endpoint) => endpoint.id.startsWith(asset.prefix)),
  }));

  const openEndpoint = (endpoint: Endpoint) => {
    goToEndpoint(endpoint, locale);
    close();
  };

  return (
    <>
      <aside className={`sidebar ${open ? 'is-open' : ''}`}>
        <div className="sidebar-head">
          <Brand locale={locale} t={t} />
          <button className="icon-button mobile-only" onClick={close} aria-label={t.closeNav}><X size={18} /></button>
        </div>
        <nav className="nav-groups" aria-label="API endpoints" key={`${locale}-${product}-${selected.group}-${isMcp}`}>
          <a className="nav-overview" href={`${APP_URL}/store`}>
            <span>{t.dataStore}</span>
            <ExternalLink size={13} />
          </a>
          <a className={`nav-overview mcp-nav ${isMcp ? 'is-active' : ''}`}
            href={withLocale('/mcp', locale)} aria-current={isMcp ? 'page' : undefined}
            onClick={(event) => { event.preventDefault(); navigate(withLocale('/mcp', locale)); close(); }}>
            <span>MCP</span><ArrowRight size={13} />
          </a>
          {product === 'flat-files' ? (
            <>
              <p className="nav-root-label">{t.queryTools}</p>
              <div className="nav-endpoints nav-endpoints-root">
                {flatFileUtilities.map((endpoint) => (
                  <button
                    key={endpoint.id}
                    className={!isMcp && selected.id === endpoint.id ? 'is-active' : ''}
                    onClick={() => openEndpoint(endpoint)}
                  >
                    {endpoint.title}
                  </button>
                ))}
              </div>
              <p className="nav-root-label nav-datasets-label">{t.datasetsLabel}</p>
              {flatFileAssets.map((asset) => {
                if (!asset.endpoints.length) return null;
                const assetSelected = asset.endpoints.some((endpoint) => endpoint.id === selected.id);
                return (
                  <details className="nav-asset" key={asset.prefix} open={assetSelected ? true : undefined}>
                    <summary>
                      <span>{asset.label}</span>
                      <ChevronDown size={14} />
                    </summary>
                    <div className="nav-asset-body nav-endpoints">
                      {asset.endpoints.map((endpoint) => (
                        <button
                          key={endpoint.id}
                          className={!isMcp && selected.id === endpoint.id ? 'is-active' : ''}
                          onClick={() => openEndpoint(endpoint)}
                        >
                          {endpoint.title}
                        </button>
                      ))}
                    </div>
                  </details>
                );
              })}
            </>
          ) : (
          <>
          <p className="nav-root-label">{t.marketAssetClass}</p>
          {groupsFor(product).map((group) => (
            <details className="nav-asset" key={group} open={!isMcp && selected.group === group ? true : undefined}>
              <summary>
                <span>{t.groups[group]}</span>
                <ChevronDown size={14} />
              </summary>
              <div className="nav-asset-body">
                {sectionsFor(group).map((section) => {
                  const sectionEndpoints = localized.filter(
                    (endpoint) => groupsOf(endpoint).includes(group) && sectionOf(endpoint) === section,
                  );
                  if (!sectionEndpoints.length) return null;
                  const sectionSelected = sectionEndpoints.some((endpoint) => endpoint.id === selected.id);
                  return (
                    <details className="nav-family" key={section} open={sectionSelected ? true : undefined}>
                      <summary>
                        <span>{t.sections[section]}</span>
                        <ChevronDown size={12} />
                      </summary>
                      <div className="nav-subsections">
                        {subsectionsFor(group, section).map((subsection) => {
                          // Endpoints native to this asset class lead; cross-listed
                          // ones follow, each keeping the catalog order.
                          const familyEndpoints = sectionEndpoints
                            .filter((endpoint) => subsectionOf(endpoint) === subsection)
                            .sort((a, b) => Number(a.group !== group) - Number(b.group !== group));
                          if (!familyEndpoints.length) return null;
                          const familySelected = familyEndpoints.some((endpoint) => endpoint.id === selected.id);
                          return (
                            <details className="nav-subfamily" key={subsection} open={familySelected ? true : undefined}>
                              <summary>
                                <span>{t.subsections[subsection]}</span>
                                <ChevronDown size={11} />
                              </summary>
                              <div className="nav-endpoints">
                                {familyEndpoints.map((endpoint) => (
                                  <button
                                    key={endpoint.id}
                                    className={!isMcp && selected.id === endpoint.id ? 'is-active' : ''}
                                    onClick={() => openEndpoint(endpoint)}
                                  >
                                    {endpoint.title}
                                  </button>
                                ))}
                              </div>
                            </details>
                          );
                        })}
                      </div>
                    </details>
                  );
                })}
              </div>
            </details>
          ))}
          </>
          )}
        </nav>
        <div className="sidebar-foot">
          <span className="status-dot" />
          <span>{t.apiOperational}</span>
          <a href={`${APP_URL}/status`}>{t.status}</a>
        </div>
      </aside>
      {open && <button className="sidebar-scrim" onClick={close} aria-label={t.closeNav} />}
    </>
  );
}

function SearchDialog({
  open,
  locale,
  t,
  localized,
  onClose,
}: {
  open: boolean;
  locale: Locale;
  t: Strings;
  localized: Endpoint[];
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return localized;
    return localized.filter((endpoint) =>
      [endpoint.title, endpoint.path, t.groups[endpoint.group], endpoint.summary, ...endpoint.parameters.map((parameter) => parameter.name)]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [query, localized, t]);

  useEffect(() => {
    if (open) {
      setQuery('');
      requestAnimationFrame(() => input.current?.focus());
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="dialog-layer" role="presentation" onMouseDown={onClose}>
      <div className="search-dialog" role="dialog" aria-modal="true" aria-label={t.searchTrigger} onMouseDown={(event) => event.stopPropagation()}>
        <div className="search-input">
          <Search size={18} />
          <input
            ref={input}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') onClose();
              if (event.key === 'Enter' && results[0]) {
                goToEndpoint(results[0], locale);
                onClose();
              }
            }}
            placeholder={t.searchPlaceholder}
          />
          <kbd>ESC</kbd>
        </div>
        <div className="search-results">
          {results.length ? results.map((endpoint) => (
            <button
              key={endpoint.id}
              onClick={() => {
                goToEndpoint(endpoint, locale);
                onClose();
              }}
            >
              <span className="method">{endpoint.resourceLabel ?? endpoint.method}</span>
              <span>
                <strong>{endpoint.title}</strong>
                <small>{t.groups[endpoint.group]} · {endpoint.resourcePath ?? endpoint.path}</small>
              </span>
              <ArrowRight size={15} />
            </button>
          )) : <p className="empty-search">{t.noMatch(query)}</p>}
        </div>
        <div className="search-hint">{t.searchHint}</div>
      </div>
    </div>
  );
}

function LanguageMenu({
  locale,
  t,
  onSelect,
}: {
  locale: Locale;
  t: Strings;
  onSelect: (next: Locale) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div className="lang-menu" ref={root}>
      <button
        className="lang-toggle"
        onClick={() => setOpen((value) => !value)}
        aria-label={t.languageLabel}
        title={t.languageLabel}
        aria-expanded={open}
      >
        <Languages size={16} />
        {localeInfo[locale].short}
      </button>
      {open && (
        <ul className="lang-list">
          {LOCALES.map((item) => (
            <li key={item}>
              <a
                href={item === locale ? undefined : '#'}
                hrefLang={localeInfo[item].hreflang}
                lang={localeInfo[item].hreflang}
                aria-current={item === locale ? 'true' : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  setOpen(false);
                  onSelect(item);
                }}
              >
                {localeInfo[item].label}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CopyButton({ value, t, label }: { value: string; t: Strings; label?: string }) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <button
      className="copy-button"
      aria-live="polite"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setFailed(false);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        } catch {
          setFailed(true);
        }
      }}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? t.copied : failed ? 'Copy failed — retry' : label ?? t.copy}
    </button>
  );
}

function ParameterTable({ endpoint, t }: { endpoint: Endpoint; t: Strings }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t.paramColParameter}</th>
            <th>{t.paramColType}</th>
            <th>{t.paramColDescription}</th>
          </tr>
        </thead>
        <tbody>
          {endpoint.parameters.map((parameter) => (
            <tr key={parameter.name}>
              <td>
                <code>{parameter.name}</code>
                {parameter.required && <span className="required">{t.required}</span>}
              </td>
              <td><span className="type">{parameter.type}</span></td>
              <td>
                {parameter.description}
                {parameter.example && <small className="example">{t.example} <code>{parameter.example}</code></small>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FieldList({ endpoint, t }: { endpoint: Endpoint; t: Strings }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? endpoint.fields : endpoint.fields.slice(0, 10);
  return (
    <>
      <div className="field-list">
        {visible.map((field) => (
          <div className="field" key={field.name}>
            <div>
              <code>{field.name}</code>
              <span className="type">{field.type}</span>
            </div>
            <p>{field.description}</p>
          </div>
        ))}
      </div>
      {endpoint.fields.length > 10 && (
        <button className="show-more" onClick={() => setExpanded((value) => !value)}>
          {expanded ? t.showFewer : t.showAllFields(endpoint.fields.length)}
          <ChevronDown className={expanded ? 'rotated' : ''} size={15} />
        </button>
      )}
    </>
  );
}

function DatasetCatalog({ endpoint, locale }: { endpoint: Endpoint; locale: Locale }) {
  if (!endpoint.relatedDatasets?.length) return null;
  const headingId = `datasets-heading-${endpoint.id}`;
  return (
    <section className="dataset-catalog" aria-labelledby={headingId}>
      <div className="section-heading">
        <div>
          <p>00</p>
          <h2 id={headingId}>Available datasets</h2>
        </div>
        <span>{endpoint.relatedDatasets.length} fixed datasets</span>
      </div>
      <div className="dataset-grid">
        {endpoint.relatedDatasets.map((dataset) => {
          const target = endpoints.find((item) => item.id === dataset.id);
          return (
            <button key={dataset.id} onClick={() => target && goToEndpoint(target, locale)}>
              <span className="dataset-title">
                <strong>{dataset.title}</strong>
                <em>{dataset.minPlan}+</em>
              </span>
              <p>{dataset.description}</p>
              <code>{dataset.path}</code>
              <ArrowRight size={15} />
            </button>
          );
        })}
      </div>
    </section>
  );
}

type RequestTab = 'browser' | 'curl' | 'python' | 'typescript';

function RequestPanel({ endpoint, t }: { endpoint: Endpoint; t: Strings }) {
  const [tab, setTab] = useState<RequestTab>('browser');

  // The API accepts the key as a query parameter too, which is what makes a
  // plain address-bar request possible. Header auth stays the default in every
  // other snippet, because a URL-borne key lands in browser history and logs.
  // Flat-file datasets are S3 and CLI workflows rather than a REST GET, so they
  // keep their own examples and no browser URL.
  const browsable = /^https:\/\//.test(endpoint.request) && !endpoint.codeExamples;
  const browserUrl = browsable
    ? `${endpoint.request}${endpoint.request.includes('?') ? '&' : '?'}apiKey=YOUR_EROYA_API_KEY`
    : '';
  const tabs: RequestTab[] = browsable
    ? ['browser', 'curl', 'python', 'typescript']
    : ['curl', 'python', 'typescript'];
  // Endpoint pages reuse this component, so a tab that the next endpoint does
  // not offer falls back instead of rendering blank.
  const active = tabs.includes(tab) ? tab : tabs[0];

  const generated: Record<RequestTab, string> = {
    browser: browserUrl,
    curl: `curl --request GET \\\n  --url '${endpoint.request}' \\\n  --header 'Authorization: Bearer $EROYA_API_KEY'`,
    python: `import requests\n\nresponse = requests.get(\n    "${endpoint.request}",\n    headers={"Authorization": "Bearer " + EROYA_API_KEY},\n)\nresponse.raise_for_status()\ndata = response.json()`,
    typescript: `const response = await fetch(\n  "${endpoint.request}",\n  { headers: { Authorization: \`Bearer \${EROYA_API_KEY}\` } },\n);\n\nif (!response.ok) throw new Error(\`HTTP \${response.status}\`);\nconst data = await response.json();`,
  };
  const code = active === 'browser'
    ? generated.browser
    : endpoint.codeExamples?.[active] ?? generated[active];

  return (
    <aside className="code-column">
      <div className="code-card request-card">
        <div className="code-head">
          <span>{t.requestLabel}</span>
          <CopyButton value={code} t={t} />
        </div>
        <div className="code-tabs">
          {tabs.map((item) => (
            <button className={active === item ? 'is-active' : ''} key={item} onClick={() => setTab(item)}>
              {item === 'browser' ? t.browserTab : item === 'curl' ? 'cURL' : item === 'python' ? 'Python' : 'TypeScript'}
            </button>
          ))}
        </div>
        <pre><code>{code}</code></pre>
        {active === 'browser' ? <p className="code-hint">{t.browserHint}</p> : null}
      </div>
      <div className="code-card response-card">
        <div className="code-head">
          <span>{t.responseLabel}</span>
          <CopyButton value={endpoint.response} t={t} />
        </div>
        <pre><code>{endpoint.response}</code></pre>
      </div>
      <div className="auth-card">
        <span>{t.authentication}</span>
        <strong>{productOf(endpoint) === 'websocket' ? 'WebSocket authentication' : t.bearerKey}</strong>
        <p>{productOf(endpoint) === 'websocket'
          ? 'Send {"action":"auth","params":"YOUR_EROYA_API_KEY"} after connecting. Subscribe after auth_success.'
          : t.authDescription}</p>
        <a href={`${APP_URL}/console`}>{t.manageKeys} <ArrowRight size={13} /></a>
      </div>
    </aside>
  );
}

function StreamPlayground({ endpoint }: { endpoint: Endpoint }) {
  const market = endpoint.id.includes('indices') ? 'indices' : endpoint.id.includes('options') ? 'options' : 'stocks';
  const event = endpoint.fields.find((field) => field.name === 'ev')?.type.match(/\((A|AM|T|Q|V)\)/)?.[1] || 'AM';
  const symbol = market === 'indices' ? 'I:SPX' : market === 'stocks' ? 'AAPL'
    : event === 'Q' ? 'O:SPY261218C00650000' : '*';
  const query = new URLSearchParams({ market, topics: `${event}.${symbol}` });
  return (
    <section aria-label="WebSocket playground">
      <iframe key={endpoint.id} title="WebSocket playground" src={`/websocket-playground.html?${query}`}
        style={{ width: '100%', height: 960, border: '1px solid var(--border)', borderRadius: 12 }} />
    </section>
  );
}

function EndpointPage({
  endpoint,
  locale,
  t,
  localized,
}: {
  endpoint: Endpoint;
  locale: Locale;
  t: Strings;
  localized: Endpoint[];
}) {
  const position = localized.findIndex((item) => item.id === endpoint.id);
  const previous = localized[position - 1];
  const next = localized[position + 1];
  const resource = endpoint.resourcePath ?? `${API_BASE}${endpoint.path}`;

  useEffect(() => {
    syncDocumentHead(endpoint, locale, t);
  }, [endpoint, locale, t]);

  return (
    <div className="page-grid">
      <main className="content">
        <div className="breadcrumbs">
          <span>{t.products[productOf(endpoint)]}</span>
          <i>/</i>
          <span>{t.groups[endpoint.group]}</span>
        </div>
        <h1>{endpoint.title}</h1>
        <p className="summary">{endpoint.summary}</p>
        <div className="endpoint-bar">
          <span className="method">{endpoint.resourceLabel ?? endpoint.method}</span>
          <code>{resource}</code>
          <CopyButton value={resource} t={t} label={endpoint.resourcePath ? t.copy : t.copyUrl} />
        </div>
        <p className="description">{endpoint.description}</p>
        <div className="use-cases">
          <span>{t.useCases}</span>
          {endpoint.useCases.map((item) => <em key={item}>{item}</em>)}
        </div>

        {productOf(endpoint) === 'websocket' && <StreamPlayground endpoint={endpoint} />}

        <DatasetCatalog endpoint={endpoint} locale={locale} />

        <section id="parameters">
          <div className="section-heading">
            <div>
              <p>01</p>
              <h2>{t.parameters}</h2>
            </div>
            <span>{t.total(endpoint.parameters.length)}</span>
          </div>
          <ParameterTable endpoint={endpoint} t={t} />
        </section>

        <section id="response">
          <div className="section-heading">
            <div>
              <p>02</p>
              <h2>{t.response}</h2>
            </div>
            <span>{endpoint.responseMediaType ?? t.applicationJson}</span>
          </div>
          <FieldList endpoint={endpoint} t={t} />
        </section>

        {endpoint.notes?.length ? (
          <section className="notes">
            <p>{t.implementationNotes}</p>
            <ul>{endpoint.notes.map((note) => <li key={note}>{note}</li>)}</ul>
          </section>
        ) : null}

        <nav className="page-nav" aria-label="Adjacent endpoints">
          {previous ? (
            <button onClick={() => goToEndpoint(previous, locale)}>
              <ArrowLeft size={16} />
              <span><small>{t.previous}</small>{previous.title}</span>
            </button>
          ) : <span />}
          {next ? (
            <button className="next" onClick={() => goToEndpoint(next, locale)}>
              <span><small>{t.next}</small>{next.title}</span>
              <ArrowRight size={16} />
            </button>
          ) : <span />}
        </nav>
      </main>
      <RequestPanel endpoint={endpoint} t={t} />
    </div>
  );
}

function McpPage({ locale, t }: { locale: Locale; t: Strings }) {
  useEffect(() => {
    syncDocumentHead({ ...endpoints[0], title: mcpGuide.title, summary: mcpGuide.summary,
      description: mcpGuide.status }, locale, t, '/mcp');
  }, [locale, t]);
  return (
    <main className="content mcp-content" lang="en">
      <div className="breadcrumbs"><span>{t.dataStore}</span><i>/</i><span>MCP</span></div>
      <h1>{mcpGuide.title}</h1>
      <p className="summary">{mcpGuide.summary}</p>
      <p className="mcp-notice">{mcpGuide.status}</p>
      <section className="mcp-start" aria-label="AI quick start">
        <p>Copy, paste to your agent, and you’re ready to go. Sign in with an email code when MCP launches.</p>
        <CopyButton value={mcpGuide.prompt} t={t} label="Copy" />
      </section>
      <section>
        <h2>Specifications</h2>
        <div className="mcp-resources">{mcpGuide.resources.map(resource => (
          <a key={resource.path} href={resource.path}><strong>{resource.label} <ArrowRight size={14} /></strong><span>{resource.description}</span></a>
        ))}</div>
      </section>
      <details className="mcp-details"><summary>Modes, coverage, and limits</summary>
        {mcpGuide.notes.map(note => <p key={note}>{note}</p>)}
      </details>
      <details className="mcp-details"><summary>Legacy API access</summary>
        <p>{mcpGuide.legacy.description}</p>
        <a href={mcpGuide.legacy.path}>{mcpGuide.legacy.label}</a>
      </details>
    </main>
  );
}

export function App() {
  const [locale, setLocale] = useState<Locale>(() => localeFromPath(window.location.pathname));
  const [endpoint, setEndpoint] = useState(currentEndpoint);
  const [isMcp, setIsMcp] = useState(() => stripLocale(window.location.pathname).replace(/\/$/, '') === '/mcp');
  const [searchOpen, setSearchOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const stored = window.localStorage.getItem('eroya-docs-theme');
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const t = ui[locale];
  // The active product follows the open endpoint, so deep links and search
  // results select the right tab without any extra state to keep in sync.
  const product = productOf(endpoint);
  const localized = useMemo(
    () => orderEndpoints(endpoints.map((item) => localizeEndpoint(item, locale))),
    [locale],
  );
  const productEndpoints = useMemo(
    () => localized.filter((item) => productOf(item) === product),
    [localized, product],
  );
  const localizedEndpoint = useMemo(() => localizeEndpoint(endpoint, locale), [endpoint, locale]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem('eroya-docs-theme', theme);
  }, [theme]);

  useEffect(() => {
    const update = () => {
      setLocale(localeFromPath(window.location.pathname));
      setEndpoint(currentEndpoint());
      setIsMcp(stripLocale(window.location.pathname).replace(/\/$/, '') === '/mcp');
    };
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setNavOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Picking a language is a durable choice: record it so the edge geo redirect
  // stops second-guessing the visitor on later visits.
  const selectLocale = (next: Locale) => {
    document.cookie = `eroya-lang=${next}; path=/; max-age=31536000; samesite=lax`;
    navigate(withLocale(isMcp ? '/mcp' : endpointHref(endpoint), next));
  };

  return (
    <div className="app-shell">
      <Sidebar isMcp={isMcp} selected={endpoint} product={product} locale={locale} t={t} localized={productEndpoints} open={navOpen} close={() => setNavOpen(false)} />
      <div className="app-main">
        <header className="topbar">
          <button className="icon-button mobile-only" onClick={() => setNavOpen(true)} aria-label={t.openNav}><Menu size={19} /></button>
          <div className="mobile-only"><Brand locale={locale} t={t} /></div>
          <button className="search-trigger" onClick={() => setSearchOpen(true)}>
            <Search size={16} />
            <span>{t.searchTrigger}</span>
            <kbd>⌘ K</kbd>
          </button>
          <nav className="product-tabs" aria-label={t.productLabel}>
            {products.map((item) => (
              <button
                key={item}
                className={!isMcp && product === item ? 'is-active' : ''}
                aria-current={!isMcp && product === item ? 'page' : undefined}
                onClick={() => goToEndpoint(landingEndpoint(item), locale)}
              >
                {t.products[item]}
              </button>
            ))}
          </nav>
          <LanguageMenu locale={locale} t={t} onSelect={selectLocale} />
          <button
            className="theme-toggle"
            onClick={() => setTheme((value) => value === 'light' ? 'dark' : 'light')}
            aria-label={t.useTheme(theme === 'light' ? themeLabel[locale].dark : themeLabel[locale].light)}
            title={t.useTheme(theme === 'light' ? themeLabel[locale].dark : themeLabel[locale].light)}
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          <div className="top-links">
            <a href="/openapi.json"><FileJson size={14} /> OPENAPI</a>
            <a href="/asyncapi.json"><FileJson size={14} /> ASYNCAPI</a>
            <a href={withLocale('/mcp', locale)} onClick={(event) => { event.preventDefault(); navigate(withLocale('/mcp', locale)); }}><FileJson size={14} /> MCP</a>
            <a className="console-link" href={`${APP_URL}/console`}>{t.console} <ArrowRight size={14} /></a>
          </div>
        </header>
        {isMcp ? <McpPage locale={locale} t={t} /> : <EndpointPage endpoint={localizedEndpoint} locale={locale} t={t} localized={localized} />}
      </div>
      <SearchDialog open={searchOpen} locale={locale} t={t} localized={localized} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
